import React, { useEffect } from 'react';
import {
  useDataModel,
  params,
  string as stringType,
  shape,
  arrayOf,
  number,
} from '@thd-nucleus/data-sources';
import { string } from 'prop-types';
import {
  Image, Card, CardContent, Placeholder
} from '@thd-olt-component-react/core-ui';
import { getCookie } from '@analytics/cookie-utils';
import {
  getFormattedOrderDate,
  getUserEmail,
  getAuthStatus,
  getUserName,
  isPersisted,
} from '../utils';
import { RecentOrderFallback } from './RecentOrderFallback';
import { RecentOrderContent } from './RecentOrderContent';
import { RecentOrderNoAuth } from './RecentOrderNoAuth';
import styles from './recent-order-details.module.scss';

const getDaysSinceOrderDate = (dateObject) => {
  const todaysDate = new Date();
  const orderDay = dateObject;

  // To calculate the time difference of two dates
  const differenceInTime = todaysDate.getTime() - orderDay.getTime();
  // To calculate the # of days between two dates
  const differenceInDays = differenceInTime / (1000 * 3600 * 24);
  return Math.floor(differenceInDays);
};

const getOrderPlacedText = (date) => {
  const daysSinceOrderDate = getDaysSinceOrderDate(date);

  if (daysSinceOrderDate === 0) return 'Placed Today';
  if (daysSinceOrderDate === 1) return 'Placed Yesterday';
  return `Placed ${daysSinceOrderDate} days ago`;
};

const getProductImages = (itemsArray) => {
  let newItemsArray = itemsArray;
  if (newItemsArray.length > 2) {
    newItemsArray = newItemsArray.slice(0, 2);
  }
  return newItemsArray.map((item, index) => {
    return (
      <Image key={item.description + index} src={item.imageUrl} alt="Missing" />
    );
  });
};

const getMoreItemsText = (itemCount) => {
  if (itemCount < 3) return '';
  if (itemCount === 3) return '+1 more item';
  return `+${itemCount - 2} more items`;
};

function getThreatMetrixId() {
  if (typeof window === 'undefined') return ''; // not sure if you want undefined or null here?

  return window.THD_ACCOUNT_LIB?.TMX?.sessionId;
  // Uncomment the below to run locally, and comment out the above but make sure not to push
  // a new branch with these changed
  // return 'be64e2ea-2da6-44eb-8934-d2fb876ecc12';
}

const RecentOrderDetails = ({ userName, userEmail }) => {
  const isNotAuthenticated = getAuthStatus();
  // eslint-disable-next-line camelcase
  const thd_user = `${getCookie('THD_USER')}`;
  // eslint-disable-next-line camelcase
  const tmx_id = getThreatMetrixId();

  const { data, loading, error } = useDataModel('orders', {
    variables: { thd_user, tmx_id },
    context: { withAuth: true },
    ssr: true
  });

  useEffect(() => {
    if (!loading) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
        'recent-order-details.ready',
        data
      );
    }
  }, [loading]);

  if (loading) {
    return (
      <Card>
        <div className={styles['recent-order-details__placeholder-title']}>
          <Placeholder type="text" height="100%" width="100%" />
        </div>
        <CardContent className="u-width-100">
          <div className={styles['recent-order-details__placeholder-container']}>
            <Placeholder type="text" />
            <Placeholder type="text" />
            {/* eslint-disable-next-line max-len */}
            <Placeholder className={styles['recent-order-details__placeholder-rect']} type="rect" height="170px" width="100%" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const orders = data?.orders ? data.orders[0] : null;
  const userAuthStatus = orders ? orders.userAuthStatus : null;
  const isUserPersisted = isPersisted();
  const customerName = userName || getUserName();
  const customerEmail = userEmail || getUserEmail();

  if (
    isNotAuthenticated
    || error?.message?.includes('401')
    || userAuthStatus === 'User is un-authorized'
  ) {
    return (
      <RecentOrderNoAuth
        customerName={isUserPersisted ? customerName : null}
        customerEmail={isUserPersisted ? customerEmail : null}
      />
    );
  }

  if (!orders || orders.length === 0) {
    return <RecentOrderFallback isError={!data} />;
  }

  const splitDate = orders.orderDate.split('-');
  const orderDateObject = new Date(
    splitDate[0],
    splitDate[1] - 1,
    splitDate[2]
  );

  const formattedOrderDate = getFormattedOrderDate(orderDateObject);
  const orderPlacedText = getOrderPlacedText(orderDateObject);
  const productImages = getProductImages(orders.lineItems.lineItems);
  const moreItemsText = getMoreItemsText(orders.lineItems.lineItemCount);

  return (
    <RecentOrderContent
      orderPlacedText={orderPlacedText}
      formattedOrderDate={formattedOrderDate}
      productImages={productImages}
      moreItemsText={moreItemsText}
    />
  );
};

RecentOrderDetails.displayName = 'RecentOrderDetails';

RecentOrderDetails.propTypes = {
  userName: string,
  userEmail: string,
};

RecentOrderDetails.defaultProps = {
  userName: null,
  userEmail: null,
};

RecentOrderDetails.dataModel = {
  orders: params({
    thd_user: stringType().isRequired(),
    tmx_id: stringType().isRequired(),
  }).arrayOf({
    userAuthStatus: stringType(),
    orderDate: stringType(),
    lineItems: shape({
      lineItemCount: number(),
      lineItems: arrayOf(
        shape({
          description: stringType(),
          imageUrl: stringType(),
        })
      ),
    }),
  }),
};

export { RecentOrderDetails };
